import { ReactNode } from 'react'
import { MessageCircle01Icon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next/pages'
import useUser from 'shared/hooks/use-user'
import { timeSince } from 'shared/utils/date-time/time-since'
import { PostCommentInfoResult } from 'modules/community/types/post-info'
import CommentAuthorPreview from './comment-author-preview'

interface PostCommentsInfoProps {
  info?: PostCommentInfoResult
}

export default function PostCommentsInfo({ info }: PostCommentsInfoProps): ReactNode {
  const { user } = useUser()
  const { t } = useTranslation()

  if (!info) {
    return <PostCommentsInfoSkeleton />
  }

  if (!info.ok)
    return (
      <div
        data-testid="post-comments-info"
        className="group flex items-center gap-2 rounded-md px-1.5 py-2 transition-colors hover:bg-bg-accent/20"
      >
        <MessageCircle01Icon className="size-5 text-text-secondary transition-colors group-hover:text-text-link" />
      </div>
    )

  const infoValue = info.value
  const commentsCount = infoValue.numberOfComments

  return (
    <div data-testid="post-comments-info" className="flex items-center gap-2">
      <div className="group flex items-center gap-2 rounded-md px-1.5 py-2 transition-colors hover:bg-bg-accent/20">
        <MessageCircle01Icon className="size-5 text-text-secondary transition-colors group-hover:text-text-link" />
        {commentsCount > 0 ? commentsCount : ''}
      </div>
      <div className="hidden gap-0.5 sm:flex lg:flex">
        {infoValue.recentCommentators.map(author => (
          <CommentAuthorPreview author={author} key={`comment_author_${author.userId}_avatar`} />
        ))}
      </div>
      {infoValue.lastCommentPostedAt && (
        <span className="text-text-link">
          {t('community.post_announce.post_info.last_comment_time', {
            time: timeSince(new Date(infoValue.lastCommentPostedAt), {
              locale: user?.dashboardLocale,
              numeric: 'always',
            }),
          })}
        </span>
      )}
    </div>
  )
}

function PostCommentsInfoSkeleton(): ReactNode {
  return <div className="h-7 w-52 animate-pulse rounded-sm bg-bg-tertiary" />
}
